// @ts-nocheck
import { useState, useEffect, useCallback, useRef } from 'react';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Separator } from '../ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  CreditCard, 
  Download, 
  TrendingUp, 
  Zap,
  AlertTriangle,
  CheckCircle,
  FileText,
  Mail,
  Crown,
  Star,
  Cpu,
  Database,
  BarChart3,
  Loader2,
  ExternalLink,
  Clock,
  XCircle,
} from 'lucide-react';
import { useWorkspace, useRegionalSettings, type Company } from '@/contexts/context';
import { supabase } from '@saas-boilerplate/shared';
import { formatCurrency as formatWorkspaceCurrency, formatDate } from '@/lib/formatting';
import { useTrialStatus } from '@/hooks/use-trial-status';

const SHOW_USAGE_AND_COSTS = false;

interface StripeSubscription {
  id: string;
  status: string;
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
  canceled_at: number | null;
  plan_amount: number | null;
  plan_currency: string | null;
  plan_interval: string | null;
  plan_price_id: string | null;
  plan_name: string | null;
}

interface StripePaymentMethod {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

interface StripeInvoice {
  id: string;
  number: string | null;
  created: number;
  period_start: number;
  period_end: number;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: string | null;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
}

function derivePlan(
  plan?: string,
  companySize?: string
): { name: string; price: number | null; priceLabel: string } {
  const key = plan || (companySize === '1-50' ? 'startup' : companySize === '51-150' ? 'scaleup' : 'poa');
  if (key === 'startup') return { name: 'Startup', price: 50, priceLabel: '£50/month' };
  if (key === 'scaleup') return { name: 'Scaleup', price: 150, priceLabel: '£150/month' };
  return { name: 'POA', price: null, priceLabel: 'Contact us' };
}

function formatUnixDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatStripeAmount(amountInCents: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountInCents / 100);
}

function formatPeriod(start: number, end: number): string {
  const s = new Date(start * 1000);
  const e = new Date(end * 1000);
  const startMonth = s.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  const endMonth = e.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  return startMonth === endMonth ? startMonth : `${startMonth} – ${endMonth}`;
}

interface BillingSubscriptionsProps {
  checkoutResult?: 'success' | 'canceled' | null;
  onCheckoutResultClose?: () => void;
}

export function BillingSubscriptions({ checkoutResult: checkoutResultProp, onCheckoutResultClose }: BillingSubscriptionsProps = {}) {
  const { workspace, company, refreshWorkspace } = useWorkspace();
  const regionalSettings = useRegionalSettings();
  const { daysRemaining, isTrialActive, isTrialExpired, hasActiveSubscription, isBillingExempt } = useTrialStatus();
  const planKey = company?.plan;
  const companySize = company?.company_size;
  const currentPlan = derivePlan(planKey, companySize);

  const [showChangePlanDialog, setShowChangePlanDialog] = useState(false);
  const confettiFiredRef = useRef(false);
  const checkoutResult = checkoutResultProp ?? null;

  const plans = [
    { key: 'startup', name: 'Startup', price: 50, description: 'For teams of 1–50 employees', popular: true },
    { key: 'scaleup', name: 'Scaleup', price: 150, description: 'For teams of 51–150 employees', popular: false },
    { key: 'poa', name: 'POA', price: null, description: 'For teams of 151+ employees', popular: false }
  ];

  const stripeCustomerId = company?.stripe_customer_id as string | undefined;
  const stripeSubscriptionStatus = company?.stripe_subscription_status as string | undefined;

  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [isChangingPlan, setIsChangingPlan] = useState(false);

  // Stripe subscription data
  const [subscription, setSubscription] = useState<StripeSubscription | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);

  // Stripe payment method
  const [paymentMethod, setPaymentMethod] = useState<StripePaymentMethod | null>(null);

  // Stripe invoices
  const [invoices, setInvoices] = useState<StripeInvoice[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);

  // Derive active plan key from Stripe subscription name (source of truth), falling back to local settings.
  // Returns null when there is no subscription (e.g. trial users) so no plan card is highlighted.
  const activePlanKey: string | null = (() => {
    if (subscription?.plan_name) {
      const name = subscription.plan_name.toLowerCase();
      if (name.includes('startup')) return 'startup';
      if (name.includes('scaleup') || name.includes('scale-up') || name.includes('scale up')) return 'scaleup';
      return 'poa';
    }
    if (planKey) return planKey;
    if (stripeSubscriptionStatus === 'active') {
      return companySize === '1-50' ? 'startup' : companySize === '51-150' ? 'scaleup' : 'poa';
    }
    return null;
  })();

  // Refresh workspace once when checkout succeeds (ref prevents loop from refreshWorkspace re-creating)
  const hasRefreshedForCheckoutRef = useRef(false);
  useEffect(() => {
    if (checkoutResult === 'success' && !hasRefreshedForCheckoutRef.current) {
      hasRefreshedForCheckoutRef.current = true;
      refreshWorkspace();
    }
    if (checkoutResult === null) hasRefreshedForCheckoutRef.current = false;
  }, [checkoutResult, refreshWorkspace]);

  // Fire confetti when success modal opens (Teamtailor-style celebration)
  useEffect(() => {
    if (checkoutResult !== 'success' || confettiFiredRef.current) return;
    confettiFiredRef.current = true;

    const colors = ['#245f73', '#2d7a91', '#3a9ab5', '#10b981', '#f59e0b', '#ec4899'];

    // Initial burst from center
    confetti({
      particleCount: 80,
      spread: 90,
      origin: { y: 0.6 },
      colors,
    });

    // Side cannons for a few seconds (Teamtailor-style)
    const duration = 2500;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors,
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors,
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  }, [checkoutResult]);

  // Fetch subscription details from Stripe
  const fetchSubscription = useCallback(async () => {
    if (!workspace?.id || !stripeCustomerId) return;
    setIsLoadingSubscription(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-get-subscription', {
        body: { workspaceId: workspace.id },
      });
      if (error) throw error;
      setSubscription(data?.subscription ?? null);
      setPaymentMethod(data?.payment_method ?? null);
    } catch (err: unknown) {
      console.error('Failed to fetch subscription:', err);
    } finally {
      setIsLoadingSubscription(false);
    }
  }, [workspace?.id, stripeCustomerId]);

  // Fetch invoices from Stripe
  const fetchInvoices = useCallback(async () => {
    if (!workspace?.id || !stripeCustomerId) return;
    setIsLoadingInvoices(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-list-invoices', {
        body: { workspaceId: workspace.id },
      });
      if (error) throw error;
      setInvoices(data?.invoices ?? []);
    } catch (err: unknown) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setIsLoadingInvoices(false);
    }
  }, [workspace?.id, stripeCustomerId]);

  // Load Stripe data on mount / when customer changes
  useEffect(() => {
    fetchSubscription();
    fetchInvoices();
  }, [fetchSubscription, fetchInvoices]);

  const handleSubscribe = async (plan?: string) => {
    const checkoutPlan = plan || activePlanKey || 'startup';
    if (!workspace?.id || checkoutPlan === 'poa') return;
    setIsLoadingCheckout(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-create-checkout', {
        body: { workspaceId: workspace.id, plan: checkoutPlan },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data?.error || 'No checkout URL returned');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error('Failed to start checkout', { description: msg });
      setIsLoadingCheckout(false);
    }
  };

  const handleManageBilling = async () => {
    if (!workspace?.id || !stripeCustomerId) return;
    setIsLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-create-portal', {
        body: { workspaceId: workspace.id },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data?.error || 'No portal URL returned');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error('Failed to open billing portal', { description: msg });
      setIsLoadingPortal(false);
    }
  };

  const handleChangePlan = async (newPlanKey: string) => {
    if (!workspace?.id) return;

    // POA: just show info toast
    if (newPlanKey === 'poa') {
      toast.info('Contact us for pricing', {
        description: "For teams of 151+ employees. We'll be in touch.",
      });
      setShowChangePlanDialog(false);
      return;
    }

    // No Stripe subscription yet: redirect to checkout
    if (!subscription?.id) {
      setShowChangePlanDialog(false);
      setIsLoadingCheckout(true);
      try {
        const { data, error } = await supabase.functions.invoke('stripe-create-checkout', {
          body: { workspaceId: workspace.id, plan: newPlanKey },
        });
        if (error) throw error;
        if (data?.url) {
          window.location.href = data.url;
        } else {
          throw new Error(data?.error || 'No checkout URL returned');
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        toast.error('Failed to start checkout', { description: msg });
        setIsLoadingCheckout(false);
      }
      return;
    }

    // Has subscription: update via Stripe
    setIsChangingPlan(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-update-subscription', {
        body: { workspaceId: workspace.id, newPlan: newPlanKey },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      await refreshWorkspace();
      await fetchSubscription();
      const planName = newPlanKey === 'startup' ? 'Startup' : 'Scaleup';
      toast.success(`Switched to ${planName}`);
      setShowChangePlanDialog(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error('Failed to change plan', { description: msg });
    } finally {
      setIsChangingPlan(false);
    }
  };

  // Derived display values from Stripe subscription
  const nextBillingDate = subscription?.current_period_end
    ? formatUnixDate(subscription.current_period_end)
    : null;

  const renewalAmount = subscription?.plan_amount != null && subscription?.plan_currency
    ? formatStripeAmount(subscription.plan_amount, subscription.plan_currency)
    : null;

  const subscriptionStatusLabel = (() => {
    if (subscription?.status) {
      return subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1).replace('_', ' ');
    }
    if (stripeSubscriptionStatus === 'active') return 'Active';
    if (isBillingExempt) return 'Complimentary';
    if (isTrialExpired) return 'Trial Expired';
    if (isTrialActive) return 'Free Trial';
    return 'No Subscription';
  })();

  const subscriptionStatusVariant = (() => {
    const s = subscription?.status || stripeSubscriptionStatus;
    if (s === 'active') return 'secondary';
    if (s === 'past_due') return 'destructive';
    if (s === 'canceled') return 'outline';
    if (isBillingExempt) return 'secondary';
    if (isTrialExpired) return 'destructive';
    if (isTrialActive) return 'outline';
    return 'outline';
  })();

  const usage = {
    ai_tokens: { used: 156789, max: 250000, cost: 24.50 },
    api_calls: { used: 45892, max: 100000, cost: 0 },
    storage: { used: 2.4, max: 10, cost: 0 }
  };

  const formatNumber = (num: number) =>
    new Intl.NumberFormat(regionalSettings?.language || 'en-US').format(num);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'default';
      case 'open': return 'secondary';
      case 'void': return 'outline';
      case 'uncollectible': return 'destructive';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1>Billing & Subscriptions</h1>
          <p className="text-muted-foreground">Manage your subscription, usage, and billing information</p>
        </div>
        {isBillingExempt ? null : (hasActiveSubscription || subscription) ? (
          <Button onClick={() => setShowChangePlanDialog(true)}>
            <TrendingUp className="h-4 w-4 mr-2" />
            Change Plan
          </Button>
        ) : (
          <Button onClick={() => setShowChangePlanDialog(true)}>
            <Zap className="h-4 w-4 mr-2" />
            Subscribe Now
          </Button>
        )}
      </div>

      {/* Complimentary access banner - supersedes trial/subscribe nudges. */}
      {isBillingExempt && (
        <div className="flex items-center space-x-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-900/20">
          <Zap className="h-5 w-5 text-emerald-600" />
          <div>
            <p className="font-medium text-emerald-900 dark:text-emerald-100">Complimentary access</p>
            <p className="text-sm text-emerald-700 dark:text-emerald-200">
              This workspace is flagged as exempt from billing - no payment required.
            </p>
          </div>
        </div>
      )}

      {/* Trial Banner */}
      {isTrialActive && !hasActiveSubscription && !isBillingExempt && (
        <div className="flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-center space-x-3">
            <Clock className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-medium text-headline dark:text-amber-100">
                Free trial: {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-200">
                Subscribe to a plan to continue using SaaS App after your trial ends.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => setShowChangePlanDialog(true)}
          >
            Subscribe Now
          </Button>
        </div>
      )}

      {isTrialExpired && !hasActiveSubscription && !isBillingExempt && (
        <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <div>
              <p className="font-medium text-red-900 dark:text-red-100">
                Your free trial has expired
              </p>
              <p className="text-sm text-red-700 dark:text-red-200">
                Subscribe to a plan to continue using SaaS App.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setShowChangePlanDialog(true)}
          >
            Subscribe Now
          </Button>
        </div>
      )}

      {/* Current Plan Overview */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Crown className="h-5 w-5 text-yellow-500" />
              <CardTitle>
                {isLoadingSubscription && stripeCustomerId
                  ? 'Loading plan...'
                  : subscription?.plan_name
                    ? `Current Plan: ${subscription.plan_name}`
                    : hasActiveSubscription
                      ? `Current Plan: ${currentPlan.name}`
                      : isTrialActive
                        ? 'Free Trial'
                        : 'No Active Plan'}
              </CardTitle>
              {!(isLoadingSubscription && stripeCustomerId) && (
                <Badge variant={subscriptionStatusVariant}>{subscriptionStatusLabel}</Badge>
              )}
              {isLoadingSubscription && stripeCustomerId && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            {subscription && !isLoadingSubscription && (
              <div className="text-right">
                <div className="text-2xl font-bold">
                  {renewalAmount
                    ? renewalAmount
                    : currentPlan.price !== null
                      ? formatWorkspaceCurrency(currentPlan.price, regionalSettings)
                      : currentPlan.priceLabel}
                  {(renewalAmount || currentPlan.price !== null) && (
                    <span className="text-sm font-normal text-muted-foreground">
                      /{subscription?.plan_interval || 'month'}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          <CardDescription>
            {nextBillingDate
              ? `Your subscription renews on ${nextBillingDate}`
              : hasActiveSubscription || subscription
                ? 'Loading subscription details...'
                : isTrialActive
                  ? `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining on your free trial`
                  : 'Subscribe to a plan to get started'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(hasActiveSubscription || subscription) ? (
            <p className="text-sm text-muted-foreground">
              Pricing based on self-declared company size. Future usage-based limits may apply.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Choose a plan now and activate your subscription.
            </p>
          )}

          {(hasActiveSubscription || subscription) && stripeCustomerId && (
            <div className="flex items-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900 dark:text-blue-100">
                    {nextBillingDate
                      ? `Next billing date: ${nextBillingDate}`
                      : 'Subscription active'}
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-200">
                    {renewalAmount
                      ? `${renewalAmount} will be charged to your payment method`
                      : currentPlan.price !== null
                        ? `${formatWorkspaceCurrency(currentPlan.price, regionalSettings)} will be charged to your payment method`
                        : 'Contact us for pricing'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage & Costs - hidden until Stripe/usage tracking is ready */}
      {SHOW_USAGE_AND_COSTS && (
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Zap className="h-5 w-5" />
            <CardTitle>Usage & Costs</CardTitle>
          </div>
          <CardDescription>Current month usage and associated costs</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="tokens" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="tokens">AI Tokens</TabsTrigger>
              <TabsTrigger value="api">API Calls</TabsTrigger>
              <TabsTrigger value="storage">Storage</TabsTrigger>
            </TabsList>
            
            <TabsContent value="tokens" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-4">LLM Token Usage</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Used this month</span>
                      <span className="font-medium">{formatNumber(usage.ai_tokens.used)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Monthly allowance</span>
                      <span className="font-medium">{formatNumber(usage.ai_tokens.max)}</span>
                    </div>
                    <Progress value={(usage.ai_tokens.used / usage.ai_tokens.max) * 100} />
                    <div className="flex justify-between items-center text-sm text-muted-foreground">
                      <span>{((usage.ai_tokens.used / usage.ai_tokens.max) * 100).toFixed(1)}% used</span>
                      <span>{formatNumber(usage.ai_tokens.max - usage.ai_tokens.used)} remaining</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium mb-4">Token Breakdown</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Feedback Analysis</span>
                      <span className="text-sm font-medium">89,234 tokens</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Idea Generation</span>
                      <span className="text-sm font-medium">45,123 tokens</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Sentiment Analysis</span>
                      <span className="text-sm font-medium">22,432 tokens</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Total Cost</span>
                      <span className="font-medium text-green-600">{formatWorkspaceCurrency(usage.ai_tokens.cost, regionalSettings)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {usage.ai_tokens.used / usage.ai_tokens.max > 0.8 && (
                <div className="flex items-center space-x-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    You've used {((usage.ai_tokens.used / usage.ai_tokens.max) * 100).toFixed(0)}% of your monthly token allowance. 
                    Consider upgrading to avoid service interruption.
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="api" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-4">API Usage</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Calls this month</span>
                      <span className="font-medium">{formatNumber(usage.api_calls.used)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Monthly allowance</span>
                      <span className="font-medium">{formatNumber(usage.api_calls.max)}</span>
                    </div>
                    <Progress value={(usage.api_calls.used / usage.api_calls.max) * 100} />
                    <div className="text-sm text-muted-foreground">
                      {((usage.api_calls.used / usage.api_calls.max) * 100).toFixed(1)}% used
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium mb-4">API Endpoints</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Feedback API</span>
                      <span className="text-sm font-medium">28,451 calls</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Ideas API</span>
                      <span className="text-sm font-medium">12,890 calls</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Analytics API</span>
                      <span className="text-sm font-medium">4,551 calls</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Additional Cost</span>
                      <span className="font-medium text-green-600">Included</span>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="storage" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-4">Storage Usage</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Used storage</span>
                      <span className="font-medium">{usage.storage.used} GB</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Storage allowance</span>
                      <span className="font-medium">{usage.storage.max} GB</span>
                    </div>
                    <Progress value={(usage.storage.used / usage.storage.max) * 100} />
                    <div className="text-sm text-muted-foreground">
                      {((usage.storage.used / usage.storage.max) * 100).toFixed(1)}% used
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium mb-4">Storage Breakdown</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Attachments</span>
                      <span className="text-sm font-medium">1.8 GB</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Database</span>
                      <span className="text-sm font-medium">0.4 GB</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Backups</span>
                      <span className="text-sm font-medium">0.2 GB</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Additional Cost</span>
                      <span className="font-medium text-green-600">Included</span>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      )}

      {/* Payment Method */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <CreditCard className="h-5 w-5" />
            <CardTitle>Payment Method</CardTitle>
          </div>
          <CardDescription>Manage your payment information for billing</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-muted rounded">
                <CreditCard className="h-5 w-5" />
              </div>
              <div>
                {paymentMethod ? (
                  <>
                    <p className="font-medium">
                      {paymentMethod.brand.charAt(0).toUpperCase() + paymentMethod.brand.slice(1)} ending in {paymentMethod.last4}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Expires {String(paymentMethod.exp_month).padStart(2, '0')}/{String(paymentMethod.exp_year).slice(-2)}
                    </p>
                  </>
                ) : stripeCustomerId ? (
                  <>
                    <p className="font-medium">Payment method on file</p>
                    <p className="text-sm text-muted-foreground">
                      Manage your payment method and invoices
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium">No payment method</p>
                    <p className="text-sm text-muted-foreground">
                      Subscribe to a plan to add a payment method
                    </p>
                  </>
                )}
              </div>
            </div>
            {stripeCustomerId ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleManageBilling}
                disabled={isLoadingPortal}
              >
                {isLoadingPortal ? 'Opening...' : 'Manage payment method'}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSubscribe}
                disabled={isLoadingCheckout || activePlanKey === 'poa'}
              >
                {isLoadingCheckout ? 'Redirecting...' : 'Subscribe'}
              </Button>
            )}
          </div>

        </CardContent>
      </Card>

      {/* Billing History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FileText className="h-5 w-5" />
              <CardTitle>Billing History</CardTitle>
            </div>
            {invoices.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleManageBilling} disabled={isLoadingPortal}>
                <Mail className="h-4 w-4 mr-2" />
                Manage Invoices
              </Button>
            )}
          </div>
          <CardDescription>Download invoices and view payment history</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingInvoices ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mr-2" />
              <span className="text-sm text-muted-foreground">Loading invoices...</span>
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                {stripeCustomerId ? 'No invoices yet' : 'Subscribe to a plan to see billing history'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-medium">{invoice.number || invoice.id}</TableCell>
                    <TableCell>{formatUnixDate(invoice.created)}</TableCell>
                    <TableCell>{formatPeriod(invoice.period_start, invoice.period_end)}</TableCell>
                    <TableCell>{formatStripeAmount(invoice.amount_paid || invoice.amount_due, invoice.currency)}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusColor(invoice.status || '')}>
                        {invoice.status || 'unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        {invoice.invoice_pdf && (
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                          >
                            <a href={invoice.invoice_pdf} target="_blank" rel="noopener noreferrer" title="Download PDF">
                              <Download className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                        {invoice.hosted_invoice_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                          >
                            <a href={invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer" title="View invoice">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Checkout Result Modal (success or canceled) */}
      <Dialog
        open={checkoutResult !== null}
        onOpenChange={(open) => {
          if (!open) {
            onCheckoutResultClose?.();
            confettiFiredRef.current = false; // Allow confetti on next success
          }
        }}
      >
        <DialogContent className="sm:max-w-[720px]">
          <DialogHeader>
            <DialogTitle className="sr-only">
              {checkoutResult === 'success' ? 'Subscription activated' : 'Checkout cancelled'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {checkoutResult === 'success' ? 'Your subscription has been activated successfully.' : 'Checkout was cancelled.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            {checkoutResult === 'success' ? (
              <>
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-6">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Subscription activated!</h2>
                <p className="text-muted-foreground max-w-md mb-8">
                  Your subscription is now active. You have full access to SaaS App. Thank you for choosing us!
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mb-6">
                  <XCircle className="h-12 w-12 text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Checkout cancelled</h2>
                <p className="text-muted-foreground max-w-md mb-8">
                  No worries - you can subscribe at any time from this page when you're ready.
                </p>
              </>
            )}
            <Button
              onClick={() => {
                onCheckoutResultClose?.();
                confettiFiredRef.current = false;
              }}
              className="min-w-[200px] h-11"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={showChangePlanDialog} onOpenChange={setShowChangePlanDialog}>
        <DialogContent className="sm:max-w-[720px]">
          <DialogHeader>
            <DialogTitle>{(hasActiveSubscription || subscription) ? 'Change Plan' : 'Select a Plan'}</DialogTitle>
            <DialogDescription>
              {(hasActiveSubscription || subscription)
                ? 'Choose the plan that fits your workspace.'
                : 'Choose a plan to get started with SaaS App.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
            {plans.map((plan) => {
              const isCurrent = activePlanKey !== null && plan.key === activePlanKey;
              return (
                <div
                  key={plan.key}
                  className={`relative flex flex-col rounded-lg border p-5 ${
                    isCurrent
                      ? 'border-primary bg-primary/5 ring-1 ring-primary'
                      : plan.popular
                        ? 'border-primary/50 shadow-sm'
                        : 'border-border'
                  }`}
                >
                  {plan.popular && !isCurrent && (
                    <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs px-2 py-0.5">
                      <Star className="h-3 w-3 mr-1" />
                      Popular
                    </Badge>
                  )}
                  {isCurrent && (
                    <Badge variant="outline" className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-white text-primary border-primary text-xs px-2 py-0.5">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Current
                    </Badge>
                  )}

                  <div className="text-center mb-4 mt-1">
                    <h3 className="text-lg font-semibold">{plan.name}</h3>
                    <div className="mt-2 mb-1">
                      {plan.price !== null ? (
                        <span className="text-2xl font-bold">
                          {formatWorkspaceCurrency(plan.price, regionalSettings)}
                          <span className="text-sm font-normal text-muted-foreground">/mo</span>
                        </span>
                      ) : (
                        <span className="text-lg font-semibold text-muted-foreground">Contact us</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{plan.description}</p>
                  </div>

                  <div className="mt-auto pt-2">
                    <Button
                      className="w-full"
                      variant={isCurrent ? 'outline' : plan.popular ? 'default' : 'outline'}
                      disabled={isCurrent || isChangingPlan || isLoadingCheckout}
                      onClick={() => handleChangePlan(plan.key)}
                    >
                      {(isChangingPlan || isLoadingCheckout) ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : isCurrent ? (
                        'Current Plan'
                      ) : plan.key === 'poa' ? (
                        'Contact Us'
                      ) : activePlanKey ? (
                        `Switch to ${plan.name}`
                      ) : (
                        `Get ${plan.name}`
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
